import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PropTypes from "prop-types";

import Button from "components/common/Button";
import BankDetailsModal from "./BankDetailsModal";
import PageLoader from "components/common/PageLoader";
import { showSnackbar } from "store/actions/Utility";
import {
  getBanksList,
  getEmployeeBankDetails,
  addEmployeeBankDetails,
  updateEmployeeBankDetails,
} from "services/talent/bankDetails";

import "./BankDetails.scss";

function WorkforceBankDetails({ employeeId }) {
  const dispatch = useDispatch();
  const [bankDetails, setBankDetails] = useState(null);
  const [bankDetailId, setBankDetailId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [banksList, setBanksList] = useState([]);

  const fetchBanksAndDetails = useCallback(async () => {
    if (!employeeId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const [banksResponse, employeeResponse] = await Promise.all([getBanksList(), getEmployeeBankDetails(employeeId)]);

      if (banksResponse?.payload) {
        setBanksList(banksResponse.payload);
      }

      if (employeeResponse?.payload?.bankDetails) {
        const bankDetailsData = employeeResponse.payload.bankDetails;

        const details = Array.isArray(bankDetailsData) ? bankDetailsData[0] : bankDetailsData;

        if (details) {
          const bankName =
            banksResponse?.payload?.find((bank) => (bank.id || bank.bankId) === (details.bankId || details.bank?.id))
              ?.name ||
            details.bankName ||
            "";

          setBankDetails({
            bankId: details.bankId || details.bank?.id || "",
            bankName,
            accountNumber: details.accountNumber || "",
            accountNumberLast4: details.accountNumberLast4 || "",
            pensionFund: details.pensionFund || "",
          });
          setBankDetailId(details.id || details.bankDetailId);
        }
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          message: error.message || "Failed to load bank details",
          severity: "error",
          dispatch,
        })
      );
    } finally {
      setIsLoading(false);
    }
  }, [employeeId, dispatch]);

  useEffect(() => {
    fetchBanksAndDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSaveBankDetails = async (data) => {
    try {
      setIsLoading(true);

      const apiData = {
        bankId: data.bankId,
        accountNumber: data.accountNumber,
        pensionFund: data.pensionFund,
      };

      if (bankDetailId) {
        await updateEmployeeBankDetails(employeeId, bankDetailId, apiData);
        dispatch(
          showSnackbar({
            message: "Bank details updated successfully",
            severity: "success",
            dispatch,
          })
        );
      } else {
        const response = await addEmployeeBankDetails(employeeId, apiData);
        if (response?.payload?.id || response?.payload?.bankDetailId) {
          setBankDetailId(response.payload.id || response.payload.bankDetailId);
        }
        dispatch(
          showSnackbar({
            message: "Bank details added successfully",
            severity: "success",
            dispatch,
          })
        );
      }

      const bankName = banksList.find((bank) => (bank.id || bank.bankId) === data.bankId)?.name || "";
      setBankDetails({
        ...data,
        bankName,
      });
    } catch (error) {
      dispatch(
        showSnackbar({
          message: error.message || "Failed to save bank details",
          severity: "error",
          dispatch,
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (!bankDetails) {
    return (
      <div className="bank-details-container bank-details-container--empty">
        <div className="bank-details-header">
          <div className="header-title">
            <div className="heading-h4-semibold color-text-headings-primary">Bank details</div>
          </div>
        </div>

        <div className="bank-details-empty-state">
          <AccountBalanceOutlinedIcon className="empty-state-icon" />
          <div className="para-body-m-regular color-text-body-secondary mb-1">
            This employee currently does not have any bank details added.
          </div>
          <Button label="Add Bank Details" variant="outlined" size="medium" onClick={handleOpenModal} />
        </div>

        <BankDetailsModal
          open={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveBankDetails}
          initialData={bankDetails}
          banksList={banksList}
        />
      </div>
    );
  }

  return (
    <div className="bank-details-container">
      <div className="bank-details-header">
        <div className="header-title">
          <AccountBalanceOutlinedIcon className="header-icon" />
          <div className="heading-h4-semibold color-text-headings-primary">Bank details</div>
        </div>
        <Button
          label="Edit"
          variant="outlined"
          size="small"
          startIcon={<EditOutlinedIcon />}
          onClick={handleOpenModal}
        />
      </div>

      <div className="bank-details-content">
        <div className="bank-detail-item mb-1">
          <div className="para-body-m-regular">Bank name</div>
          <div className="para-body-m-semibold">{bankDetails.bankName}</div>
        </div>

        <div className="bank-detail-item mb-1">
          <div className="para-body-m-regular">Bank account number</div>
          <div className="para-body-m-semibold">
            {`**** **** **** ${bankDetails.accountNumberLast4 || bankDetails.accountNumber?.slice(-4) || ""}`}
          </div>
        </div>

        <div className="bank-detail-item">
          <div className="para-body-m-regular">Pension fund (AFP/ONP)</div>
          <div className="para-body-m-semibold">{bankDetails.pensionFund}</div>
        </div>
      </div>

      <BankDetailsModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveBankDetails}
        initialData={bankDetails}
        banksList={banksList}
      />
    </div>
  );
}

WorkforceBankDetails.propTypes = {
  employeeId: PropTypes.string.isRequired,
};

export default WorkforceBankDetails;
